import React, { useCallback, useEffect, useState } from 'react';
import { Facebook, Instagram, Mail, X } from 'lucide-react';
import {
  isValidYoutubeId,
  musicPage,
  youtubeEmbedUrl,
  youtubeThumbnail,
} from '../data/musicPage';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">{children}</p>
  );
}

const Music: React.FC = () => {
  const { hero, release, featuredVideo, bio, contact } = musicPage;
  const [isVisualizerOpen, setIsVisualizerOpen] = useState(false);

  const hasVisualizer = isValidYoutubeId(hero.visualizerVideoId);
  const hasFeaturedVideo = isValidYoutubeId(featuredVideo.youtubeVideoId);
  const heroArtwork =
    hero.artworkUrl ??
    (hasVisualizer ? youtubeThumbnail(hero.visualizerVideoId) : null);

  const closeVisualizer = useCallback(() => setIsVisualizerOpen(false), []);

  useEffect(() => {
    if (!isVisualizerOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVisualizer();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isVisualizerOpen, closeVisualizer]);

  return (
    <div className="min-h-svh bg-black text-white">
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16 lg:py-20">
        <section className="border-b border-zinc-800 pb-12 sm:pb-16">
          <div className="flex flex-col gap-8 sm:flex-row sm:justify-between">
            <div className="max-w-xl space-y-4">
              <h1 className="font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
                {hero.title}
              </h1>
              <p className="text-base leading-relaxed text-zinc-400 sm:text-lg">
                {hero.tagline}
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a
                  href={hero.listenUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-sm border border-white bg-white px-5 py-2.5 text-sm font-medium uppercase tracking-wider text-black transition hover:bg-zinc-200"
                >
                  Listen Now
                </a>
                <div className="flex items-center gap-2">
                  <a
                    href={hero.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 transition hover:border-zinc-500 hover:text-white"
                  >
                    <Instagram className="h-4 w-4" aria-hidden />
                  </a>
                  <a
                    href={hero.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 transition hover:border-zinc-500 hover:text-white"
                  >
                    <Facebook className="h-4 w-4" aria-hidden />
                  </a>
                  <a
                    href={`mailto:${hero.social.email}?subject=After Selection — inquiry`}
                    aria-label="Email"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 transition hover:border-zinc-500 hover:text-white"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                  </a>
                </div>
              </div>
            </div>

            {heroArtwork ? (
              <div className="relative mx-auto aspect-square w-full shrink-0 overflow-hidden rounded-sm border border-zinc-800 bg-zinc-900 sm:mx-0 sm:w-48">
                <img
                  src={heroArtwork}
                  alt="C.M. Samuels"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            ) : null}
          </div>
        </section>

        <section id="release" className="border-b border-zinc-800 py-12 sm:py-16">
          <SectionLabel>Release</SectionLabel>

          <div className="mt-6 rounded-sm border border-zinc-800 bg-zinc-950">
            <div className="flex flex-col gap-8 sm:flex-row sm:gap-10">
              {release.artworkUrl ? (
                <img
                  src={release.artworkUrl}
                  alt={release.title}
                  className="aspect-square w-full shrink-0 object-cover sm:w-80"
                />
              ) : null}

              <div className="min-w-0 flex-1 px-6 pb-6 sm:px-0 sm:py-8">
                <h2 className="font-serif text-2xl tracking-tight sm:text-3xl">
                  {release.title}
                </h2>

                <ul className="mt-6 flex flex-col gap-3">
                  {release.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-zinc-400 underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-zinc-400"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="featured-video" className="border-b border-zinc-800 py-12 sm:py-16">
          <SectionLabel>Featured video</SectionLabel>
          <h2 className="mt-3 font-serif text-2xl tracking-tight sm:text-3xl">
            {featuredVideo.title}
          </h2>

          <div className="mt-6 overflow-hidden rounded-sm border border-zinc-800 bg-zinc-950">
            {hasFeaturedVideo ? (
              <div className="relative aspect-video w-full">
                <iframe
                  title={`${featuredVideo.title} video`}
                  src={youtubeEmbedUrl(featuredVideo.youtubeVideoId)}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 px-6 text-center">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">
                  Video embed placeholder
                </p>
                <p className="max-w-sm text-xs text-zinc-600">
                  Set <code className="text-zinc-400">featuredVideo.youtubeVideoId</code> in{' '}
                  <code className="text-zinc-400">src/data/musicPage.ts</code>
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="border-b border-zinc-800 py-12 sm:py-16">
          <SectionLabel>Bio</SectionLabel>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-zinc-400 sm:text-lg">
            {bio.split('\n\n').map((paragraph) => (
              <p key={paragraph.slice(0, 24)}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <SectionLabel>Contact</SectionLabel>
          <a
            href={`mailto:${contact.email}?subject=After Selection — inquiry`}
            className="mt-4 flex items-center justify-between rounded-sm border border-zinc-700 px-4 py-3.5 text-sm transition hover:border-zinc-500 hover:bg-zinc-950"
          >
            <span className="font-medium tracking-wide">{contact.email}</span>
            <span className="hidden text-xs uppercase tracking-wider text-zinc-500 md:block">
              {contact.label}
            </span>
          </a>
        </section>
      </div>

      {isVisualizerOpen && hasVisualizer ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`${hero.title} visualizer`}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={closeVisualizer}
            aria-label="Close video"
          />
          <div className="relative z-10 w-full max-w-3xl">
            <button
              type="button"
              onClick={closeVisualizer}
              className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full border border-zinc-600 text-zinc-300 transition hover:border-zinc-400 hover:text-white"
              aria-label="Close video"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
            <div className="relative aspect-video w-full overflow-hidden rounded-sm bg-black shadow-2xl">
              <iframe
                title={`${hero.title} visualizer`}
                src={youtubeEmbedUrl(hero.visualizerVideoId, true)}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Music;
